import { useCallback, useEffect, useMemo, useState } from 'react';
import { OutreachAction, SupplierThread } from '../components/forms/outreach/types';
import { outreachClient } from '../client/outreachClient';
import { buildActionIndex, composeAskQuery, threadIdentity } from './outreachActionModel';

/**
 * The ids whose asks are sitting in a composer, per thread. Keyed by thread
 * identity so each conversation keeps its own box: staging on one thread never
 * touches another's, and a send closes only the cards its own thread staged.
 */
type StagedByThread = Record<string, string[]>;

/** Stable empty list so "nothing staged" never looks like a new value. */
const NONE: string[] = [];

const withoutThread = (staged: StagedByThread, key: string): StagedByThread => {
  const { [key]: _dropped, ...rest } = staged;
  return rest;
};

/**
 * The thread analyst's open moves for one RFQ.
 *
 * Reads ride the outreach hook's existing 20s poll (`pollNonce`) rather than a
 * second timer, so cards and replies refresh together. NOTHING a card does
 * reaches the supplier: a card writes its ask into the thread's composer and
 * stops there, one card or all of them, because the words are the buyer's to
 * edit and the send button is theirs to press. A card is marked DONE only once
 * an email has actually left.
 *
 * `kickoffPending` rides the same read: it is the server saying opening asks
 * are still being written, which is the only way an empty board can be told
 * from a finished one.
 */
export const useOutreachActions = (rfqId: string | undefined, pollNonce: number) => {
  const [actions, setActions] = useState<OutreachAction[]>([]);
  const [kickoffPending, setKickoffPending] = useState(false);
  const [staged, setStaged] = useState<StagedByThread>({});

  const fetchActions = useCallback(async () => {
    if (!rfqId) return;
    try {
      const read = await outreachClient.listOutreachActions(rfqId);
      setActions(read.actions);
      setKickoffPending(read.kickoffPending);
    } catch (err) {
      // Fail quiet like the analyst itself: the threads are still readable and
      // the composer still works — a missing card is not a broken step.
      console.error('Error fetching outreach actions:', err);
    }
  }, [rfqId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions, pollNonce]);

  const index = useMemo(() => buildActionIndex(actions), [actions]);

  /** Drops actions from the local set so their cards leave immediately. */
  const removeLocally = (actionIds: string[]) =>
    setActions((prev) => prev.filter((a) => !actionIds.includes(a.id)));

  /**
   * Closes the cards a landed send covered. The endpoint takes one action at a
   * time, so a bundle walks them; a failed close is logged, not surfaced — the
   * email went, and the next poll re-reads the truth.
   */
  const resolveDone = useCallback(
    async (actionIds: string[]) => {
      if (!rfqId || actionIds.length === 0) return;
      removeLocally(actionIds);
      for (const actionId of actionIds) {
        try {
          await outreachClient.resolveOutreachAction(rfqId, actionId, 'DONE');
        } catch (err) {
          console.error('Error resolving outreach action:', err);
        }
      }
      fetchActions();
    },
    [rfqId, fetchActions],
  );

  /** What this thread already has waiting in its box. */
  const stagedIdsFor = useCallback(
    (thread: SupplierThread | undefined): string[] => (thread ? staged[threadIdentity(thread)] ?? NONE : NONE),
    [staged],
  );

  /**
   * Asks handed to the composer rather than to the supplier — one card's or
   * several. Nothing is sent and nothing closes here; the lines are the
   * buyer's to rewrite, and the send button is theirs to press.
   *
   * Staging ACCUMULATES: an ask already in the box is not written a second
   * time, so tapping cards one by one and then "put the rest in" builds a
   * single list instead of repeating asks. The returned text is only what is
   * new — the caller appends it under whatever the buyer has already typed.
   * The counter joins only when its number is confirmed; the caller leaves it
   * out otherwise rather than staging an unapproved price.
   */
  const stageActions = useCallback(
    (thread: SupplierThread, chosen: OutreachAction[], approvedPrice?: number): string => {
      const key = threadIdentity(thread);
      const already = staged[key] ?? NONE;
      const fresh = chosen.filter((action) => !already.includes(action.id));
      if (fresh.length === 0) return '';
      setStaged((prev) => ({ ...prev, [key]: [...(prev[key] ?? NONE), ...fresh.map((action) => action.id)] }));
      return composeAskQuery(thread, fresh, approvedPrice, already.length);
    },
    [staged],
  );

  /** The box was emptied — nothing of Robbie's is sitting in it any more. */
  const clearStaging = useCallback((thread: SupplierThread) => {
    const key = threadIdentity(thread);
    setStaged((prev) => (prev[key] ? withoutThread(prev, key) : prev));
  }, []);

  /**
   * The staged ids ride along until the next send from that thread's composer,
   * whatever the buyer made of the words in between — those are their asks now.
   * Landing the send closes every id that thread accumulated. Another thread's
   * staging is untouched: its asks are still sitting in its own box, unsent.
   */
  const resolveStagedSend = useCallback(
    async (thread: SupplierThread) => {
      const key = threadIdentity(thread);
      const pending = staged[key] ?? NONE;
      if (pending.length === 0) return;
      setStaged((prev) => withoutThread(prev, key));
      await resolveDone(pending);
    },
    [staged, resolveDone],
  );

  /** "Not now" — the buyer judged this move not worth making. */
  const dismissAction = useCallback(
    async (actionId: string) => {
      if (!rfqId) return;
      removeLocally([actionId]);
      try {
        await outreachClient.resolveOutreachAction(rfqId, actionId, 'DISMISSED');
      } catch (err) {
        console.error('Error dismissing outreach action:', err);
        fetchActions();
      }
    },
    [rfqId, fetchActions],
  );

  return {
    actionIndex: index,
    kickoffPending,
    stagedIdsFor,
    stageActions,
    clearStaging,
    resolveStagedSend,
    dismissAction,
    /** Re-reads the board now, without waiting for the next poll. */
    refreshActions: fetchActions,
  };
};
